<template>
  <div class="flex flex-col gap-5">
    <!-- Header -->
    <header class="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 class="wh-display text-[22px] font-bold leading-tight tracking-tight text-gray-900 sm:text-[30px] dark:text-slate-100">
          Tableau de bord
        </h1>
        <p class="mt-0.5 text-[12px] text-gray-500 sm:text-[13px] dark:text-slate-400">
          Vue d'ensemble des trackers &amp; gateways par environnement
        </p>
      </div>
      <button class="wh-btn wh-btn-outline" @click="refreshData">
        <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
        </svg>
        Actualiser
      </button>
    </header>

    <!-- Loading / error -->
    <div v-if="loading" class="grid place-items-center py-20">
      <span class="h-7 w-7 animate-spin rounded-full border-2 border-gray-200 border-t-primary-500"></span>
    </div>
    <div v-else-if="error" class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
      {{ error }}
    </div>

    <template v-else>
      <!-- KPI cards -->
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div v-for="kpi in kpis" :key="kpi.label" class="wh-card p-4">
          <div class="flex items-center justify-between">
            <span class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">{{ kpi.label }}</span>
            <span class="wh-dot h-2.5 w-2.5" :style="{ backgroundColor: kpi.color }"></span>
          </div>
          <div class="wh-display mt-2 text-[30px] font-bold leading-none text-gray-900 dark:text-slate-50">{{ kpi.value }}</div>
        </div>
      </div>

      <!-- Charts -->
      <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <section class="wh-card p-4">
          <h2 class="mb-1 text-[15px] font-semibold text-gray-900 dark:text-slate-100">Répartition par environnement</h2>
          <div class="relative h-[300px]"><canvas ref="envChart"></canvas></div>
        </section>
        <section class="wh-card p-4">
          <h2 class="mb-1 text-[15px] font-semibold text-gray-900 dark:text-slate-100">Répartition par type</h2>
          <div class="relative h-[300px]"><canvas ref="typeChart"></canvas></div>
        </section>
      </div>

      <!-- Breakdown table -->
      <section class="wh-card overflow-hidden">
        <div class="border-b border-gray-100 px-4 py-3 dark:border-slate-700/60">
          <h2 class="text-[15px] font-semibold text-gray-900 dark:text-slate-100">Détail par environnement &amp; type</h2>
        </div>
        <div class="overflow-x-auto">
          <table class="wh-table">
            <thead>
              <tr>
                <th>Environnement</th>
                <th class="text-center">Trackers</th>
                <th class="text-center">Gateways</th>
                <th class="text-center">Non définis</th>
                <th class="text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(label, key) in envLabels" :key="key">
                <td>
                  <span class="wh-badge" :style="softStyle(envColor(key))">
                    <span class="wh-dot" :style="{ backgroundColor: envColor(key) }"></span>{{ label }}
                  </span>
                </td>
                <td class="text-center tabular-nums">{{ stats.byEnvAndType[key]?.tracker || 0 }}</td>
                <td class="text-center tabular-nums">{{ stats.byEnvAndType[key]?.gateway || 0 }}</td>
                <td class="text-center tabular-nums text-gray-500 dark:text-slate-400">{{ stats.byEnvAndType[key]?.unknown || 0 }}</td>
                <td class="wh-display text-center font-bold tabular-nums">{{ envTotal(key) }}</td>
              </tr>
              <tr class="bg-gray-50 font-bold dark:bg-slate-700/30">
                <td>Total</td>
                <td class="text-center tabular-nums">{{ stats.byType?.tracker || 0 }}</td>
                <td class="text-center tabular-nums">{{ stats.byType?.gateway || 0 }}</td>
                <td class="text-center tabular-nums">{{ stats.byType?.unknown || 0 }}</td>
                <td class="wh-display text-center tabular-nums">{{ stats.total || 0 }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import api from '../api'
import { COLORS, ENV_LABELS, envColor, typeColor, softStyle } from '../constants'
import Chart from 'chart.js/auto'

const emptyStats = () => ({
  total: 0,
  byEnvironment: { dev: 0, qualif: 0, prod: 0, other: 0 },
  byType: { tracker: 0, gateway: 0, unknown: 0 },
  byEnvAndType: {
    dev: { tracker: 0, gateway: 0, unknown: 0 },
    qualif: { tracker: 0, gateway: 0, unknown: 0 },
    prod: { tracker: 0, gateway: 0, unknown: 0 },
    other: { tracker: 0, gateway: 0, unknown: 0 },
  },
})

export default {
  name: 'Dashboard',
  data() {
    return {
      stats: emptyStats(),
      loading: true,
      error: null,
      envChart: null,
      typeChart: null,
      envLabels: ENV_LABELS,
    }
  },
  computed: {
    kpis() {
      return [
        { label: 'Total', value: this.stats.total || 0, color: COLORS.primary },
        { label: 'Trackers', value: this.stats.byType?.tracker || 0, color: COLORS.types.tracker },
        { label: 'Gateways', value: this.stats.byType?.gateway || 0, color: COLORS.types.gateway },
        { label: 'Non définis', value: this.stats.byType?.unknown || 0, color: COLORS.types.default },
      ]
    },
  },
  methods: {
    softStyle,
    envColor,
    envTotal(env) {
      const e = this.stats.byEnvAndType?.[env]
      return e ? (e.tracker || 0) + (e.gateway || 0) + (e.unknown || 0) : 0
    },
    async fetchData() {
      this.loading = true
      this.error = null
      try {
        const { data } = await api.get('/statistics')
        const base = emptyStats()
        this.stats = {
          total: data.total || 0,
          byEnvironment: { ...base.byEnvironment, ...(data.byEnvironment || {}) },
          byType: { ...base.byType, ...(data.byType || {}) },
          byEnvAndType: {
            dev: { ...base.byEnvAndType.dev, ...(data.byEnvAndType?.dev || {}) },
            qualif: { ...base.byEnvAndType.qualif, ...(data.byEnvAndType?.qualif || {}) },
            prod: { ...base.byEnvAndType.prod, ...(data.byEnvAndType?.prod || {}) },
            other: { ...base.byEnvAndType.other, ...(data.byEnvAndType?.other || {}) },
          },
        }
        this.$nextTick(() => this.createCharts())
      } catch (err) {
        console.error('Erreur lors du chargement des statistiques:', err)
        this.error = 'Impossible de charger les statistiques. Veuillez réessayer plus tard.'
      } finally {
        this.loading = false
      }
    },
    refreshData() { this.fetchData() },
    doughnutOptions() {
      const dark = document.documentElement.classList.contains('dark')
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
          legend: { position: 'right', labels: { color: dark ? '#94a3b8' : '#6b7280', usePointStyle: true, boxWidth: 8, padding: 14, font: { family: 'Lato', size: 12 } } },
          tooltip: {
            callbacks: {
              label(ctx) {
                const value = ctx.raw || 0
                const total = ctx.dataset.data.reduce((a, b) => a + b, 0)
                const pct = total > 0 ? Math.round((value / total) * 100) : 0
                return `${ctx.label}: ${value} (${pct}%)`
              },
            },
          },
        },
      }
    },
    createCharts() {
      if (this.envChart) this.envChart.destroy()
      if (this.typeChart) this.typeChart.destroy()
      if (!this.$refs.envChart || !this.$refs.typeChart) { setTimeout(() => this.createCharts(), 100); return }
      const ds = { borderWidth: 0, hoverOffset: 6 }

      this.envChart = new Chart(this.$refs.envChart.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: Object.values(ENV_LABELS),
          datasets: [{
            ...ds,
            data: [this.stats.byEnvironment.dev, this.stats.byEnvironment.qualif, this.stats.byEnvironment.prod, this.stats.byEnvironment.other],
            backgroundColor: [envColor('dev'), envColor('qualif'), envColor('prod'), COLORS.environments.default],
          }],
        },
        options: this.doughnutOptions(),
      })

      this.typeChart = new Chart(this.$refs.typeChart.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: ['Trackers', 'Gateways', 'Non définis'],
          datasets: [{
            ...ds,
            data: [this.stats.byType.tracker, this.stats.byType.gateway, this.stats.byType.unknown],
            backgroundColor: [typeColor('tracker'), typeColor('gateway'), COLORS.types.default],
          }],
        },
        options: this.doughnutOptions(),
      })
    },
  },
  mounted() { this.fetchData() },
  beforeUnmount() {
    if (this.envChart) this.envChart.destroy()
    if (this.typeChart) this.typeChart.destroy()
  },
}
</script>
